import { useMemo } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { useExpaContext } from '../../../expa/components/ExpaProvider/ExpaContextProvider'
import { useIsLocalStorageAvailable } from '../../../generic-hooks/useIsLocalStorageAvailable'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { SHOP_ENV, TENANT } from '../../../tenant/tenantTypes'
import { useExperimentsPageType } from '../../components/ExperimentsPageTypeContext'
import { ExperimentPageType } from '../../ExperimentPageType'

const BLACKLISTED_EXPAS = new Set(['dvd'])
const ERX_MODAL_ID = 'ErxModal'

/**
 * Custom hook to determine if the eRx modal should be shown.
 * It checks various conditions including local storage availability, tenant, page type, and whether the modal has been shown before.
 * @returns {Object} - An object containing a function to set the modal shown state and a boolean indicating if the modal should be shown.
 */
export const useErxModalShouldBeShown = (): {
  setErxModalShown: (value: boolean) => void
  shouldBeShown: boolean
} => {
  const [erxModalShown, setErxModalShown] = useLocalStorage(ERX_MODAL_ID, false)
  const [forceModalOnQs] = useLocalStorage('forceModalOnQs', false)
  const globalContext = useGlobalConfigContext()
  const pageType = useExperimentsPageType()
  const isLocalStorageAvailable = useIsLocalStorageAvailable()
  const expa = useExpaContext() ?? ''
  const blacklistPages: ExperimentPageType[] = useMemo(() => {
    return [
      ExperimentPageType.Account,
      ExperimentPageType.ForgotPassword,
      ExperimentPageType.Newsletter,
      ExperimentPageType.Cart,
      ExperimentPageType.Checkout,
      ExperimentPageType.Login,
      ExperimentPageType.Search,
    ]
  }, [])

  const shouldBeShown = useMemo(() => {
    if (!isLocalStorageAvailable) {
      return false
    }

    return (
      globalContext.tenant === TENANT.COM &&
      !erxModalShown &&
      !BLACKLISTED_EXPAS.has(expa) &&
      !blacklistPages.includes(pageType) &&
      (forceModalOnQs || globalContext.shopEnv === SHOP_ENV.PROD)
    )
  }, [
    isLocalStorageAvailable,
    globalContext.tenant,
    erxModalShown,
    expa,
    pageType,
    globalContext.shopEnv,
    blacklistPages,
    forceModalOnQs,
  ])

  return { setErxModalShown, shouldBeShown }
}
